import { DEFAULT_BLOCK_CSS } from "../constant/constant";
import { parseCssStringToObject } from "../helper";
import { resolveBlockCssFontFamily } from "../../shared/legacy-font-google.js";

const viewportKey = (isMobile) => (isMobile ? "mobile" : "desktop");

/** Same merge as Draggable blocks: DEFAULT + customCSS + parsed overrideCss */
export function getSectionBlockCss(section, isMobile) {
  const viewport = viewportKey(isMobile);
  return {
    ...DEFAULT_BLOCK_CSS,
    ...section?.setting?.customCSS?.[viewport],
    ...parseCssStringToObject(section?.setting?.overrideCss?.[viewport]),
  };
}

export function getSectionDimension(section, isMobile) {
  const viewport = viewportKey(isMobile);
  return section?.setting?.dimension?.[viewport] ?? {};
}

export function getSectionLayer(section, isMobile) {
  const viewport = viewportKey(isMobile);
  return section?.setting?.layer?.[viewport] ?? {};
}

/** Drop editor box sizing so flow sections can use width: 100%. */
export function stripLayoutSizingFromCss(css = {}) {
  if (!css || typeof css !== "object") {
    return {};
  }
  const {
    width,
    height,
    minWidth,
    maxWidth,
    minHeight,
    maxHeight,
    left,
    top,
    right,
    bottom,
    position,
    transform,
    ...visual
  } = css;
  return visual;
}

/** Drop editor flex centering so blocks stretch in the cart drawer flow. */
export function stripFlexLayoutFromCss(css = {}) {
  if (!css || typeof css !== "object") {
    return {};
  }
  const {
    display,
    flex,
    flexDirection,
    flexWrap,
    flexFlow,
    justifyContent,
    alignItems,
    alignContent,
    gap,
    rowGap,
    columnGap,
    ...visual
  } = css;
  return visual;
}

function resolveMarginBottom(css = {}) {
  const rawPx = css.marginBottomPx;
  if (rawPx != null && rawPx !== "") {
    const px = Number(rawPx);
    if (Number.isFinite(px) && px > 0) {
      return `${px}px`;
    }
  }
  const raw = css.marginBottom;
  if (raw == null || raw === "" || raw === 0 || raw === "0") {
    return undefined;
  }
  if (typeof raw === "number" && raw > 0) {
    return `${raw}px`;
  }
  return String(raw);
}

/** Remove padding/margin so cart drawer regions keep layout from CSS classes. */
export function stripContainerSpacing(css = {}) {
  if (!css || typeof css !== "object") {
    return {};
  }
  const {
    padding,
    paddingTop,
    paddingRight,
    paddingBottom,
    paddingLeft,
    margin,
    marginTop,
    marginRight,
    marginBottom,
    marginLeft,
    ...rest
  } = css;
  return rest;
}

/** Title row in cart header — typography only (no margins / flex). */
export function getCartDrawerHeaderTitleTypography(section, isMobile) {
  const css = getCartDrawerFlowCss(section, isMobile);
  const {
    margin,
    marginTop,
    marginRight,
    marginBottom,
    marginLeft,
    marginBottomPx,
    textAlign,
    justifyContent,
    alignItems,
    alignContent,
    alignSelf,
    display,
    width,
    height,
    minWidth,
    maxWidth,
    minHeight,
    maxHeight,
    ...typography
  } = css;
  return typography;
}

/** Maps editor title alignment to flex justify on the header title row. */
export function getCartDrawerHeaderTitleJustify(section, isMobile) {
  const viewport = viewportKey(isMobile);
  const custom = section?.setting?.customCSS?.[viewport] || {};
  const override = parseCssStringToObject(section?.setting?.overrideCss?.[viewport]);
  const jc = custom.justifyContent ?? override.justifyContent;
  const ta = custom.textAlign ?? override.textAlign;

  if (jc === "center" || ta === "center") return "center";
  if (
    jc === "flex-end" ||
    jc === "end" ||
    jc === "right" ||
    ta === "right"
  ) {
    return "flex-end";
  }
  if (
    jc === "flex-start" ||
    jc === "start" ||
    jc === "left" ||
    ta === "left"
  ) {
    return "flex-start";
  }
  return "flex-start";
}

/** Visual-only CSS for cart drawer flow (no canvas size/position/flex). */
export function getCartDrawerFlowCss(section, isMobile) {
  const css = stripFlexLayoutFromCss(
    stripLayoutSizingFromCss(
      stripCanvasPositioning(getSectionBlockCss(section, isMobile))
    )
  );

  const marginBottom = resolveMarginBottom(css);
  const { marginBottomPx, marginBottom: _mb, margin, ...rest } = css;

  if (marginBottom) {
    rest.marginBottom = marginBottom;
  }
  if (margin === 0 || margin === "0") {
    return resolveBlockCssFontFamily(rest);
  }
  return resolveBlockCssFontFamily({
    ...rest,
    ...(margin != null ? { margin } : {}),
  });
}

/** Editor canvas coords — strip for cart-drawer flow slots */
export function stripCanvasPositioning(css = {}) {
  if (!css || typeof css !== "object") {
    return {};
  }
  const {
    position,
    left,
    top,
    right,
    bottom,
    transform,
    ...flowCss
  } = css;
  return flowCss;
}

/**
 * Shell around each subsection in the cart drawer grid (mirrors DraggableWrapper box:
 * dimension + layer, without absolute canvas positioning).
 */
export function getCartDrawerSlotShellStyle(section, isMobile, extraStyle = {}) {
  const dimension = getSectionDimension(section, isMobile);
  const layer = getSectionLayer(section, isMobile);
  const flowCss = stripCanvasPositioning(getSectionBlockCss(section, isMobile));

  const useFullWidth =
    extraStyle.width === "100%" || extraStyle.maxWidth === "100%";

  // Cart drawer flow: width is always 100% so the row adapts to drawer size;
  // height still follows schema dimension when set.
  const width = useFullWidth ? "100%" : "100%";
  const height = useFullWidth
    ? extraStyle.height ?? dimension.height ?? flowCss.height ?? "auto"
    : extraStyle.height ?? dimension.height ?? flowCss.height ?? "auto";

  const { width: _w, height: _h, ...restExtra } = extraStyle;

  return {
    position: "relative",
    boxSizing: "border-box",
    maxWidth: "100%",
    minWidth: 0,
    width,
    height,
    ...(layer.zIndex != null ? { zIndex: layer.zIndex } : {}),
    ...restExtra,
  };
}

export function resolveCssPixel(value, fallback) {
  if (value == null || value === "") {
    return fallback;
  }
  if (typeof value === "number" && !Number.isNaN(value)) {
    return value;
  }
  if (typeof value === "string") {
    const parsed = parseInt(value, 10);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }
  return fallback;
}

/**
 * Image column size: prefer theme cart line item, then schema dimension.
 */
export function getCartDrawerImageColumnSize(
  imageSection,
  isMobile,
  { themeLineImage = null, fallback = 80 } = {}
) {
  if (themeLineImage?.width > 0 && themeLineImage?.height > 0) {
    return {
      width: themeLineImage.width,
      height: themeLineImage.height,
    };
  }

  if (!imageSection) {
    return { width: fallback, height: fallback };
  }

  const dimension = getSectionDimension(imageSection, isMobile);
  const blockCss = getSectionBlockCss(imageSection, isMobile);
  const width = resolveCssPixel(
    dimension.width ?? blockCss.width,
    fallback
  );
  const height = resolveCssPixel(dimension.height ?? blockCss.height, width);

  return { width, height };
}

/** @deprecated use getCartDrawerImageColumnSize */
export function getCartDrawerImageColumnWidth(imageSection, isMobile, fallback = 80) {
  return getCartDrawerImageColumnSize(imageSection, isMobile, { fallback }).width;
}
